import type { EvaluationPeriod, ModuleItemType } from 'app/types/modules';
import { EVALUATION_PERIODS, EVALUATION_PERIODS_TYPES, findEvaluationPeriodType, normalizeEvaluationPeriod } from 'core/utils/constants/periods';

type PeriodDefinition = { id: string; name: string };

export type UncoveredPeriodDiagnostics = {
  normalizedType: string;
  isUnknownType: boolean;
  extraPeriodIds: string[];
};

type PeriodLayout = UncoveredPeriodDiagnostics & { definitions: PeriodDefinition[] };

// Sección para cursos sin tipo conocido ni ids en sus items. El centinela nunca se envía al API: al crear
// se manda '' (el valor legacy, TAN-10611) y el reorder omite evaluation_period_id (TAN-11212).
export const UNASSIGNED_PERIOD_ID = '__unassigned__';

const UNASSIGNED_PERIOD_NAME = 'School year';

const PERIOD_PREFIX_NAMES: Record<string, string> = { b: 'Bimester', q: 'Quadrimester', s: 'Semester', t: 'Quarter' };

// Sin `0*` delante de los dígitos: Number() ya quita los ceros y así la regex no retrocede (sonarjs/super-linear-regex).
const PERIOD_ID_PATTERN = /^([a-z])(\d+)$/;

export function isUnassignedPeriod(id: string | null | undefined): boolean {
  return id === UNASSIGNED_PERIOD_ID;
}

/** Nombre de la sección (clave de `new-course`) para un id de periodo; el propio id si no se reconoce. */
export function getPeriodSectionName(periodId: string): string {
  const configuredPeriod = EVALUATION_PERIODS_TYPES.flatMap((type) => type.periods).find((period) => period.id === periodId);
  if (configuredPeriod) return configuredPeriod.name;

  const match = PERIOD_ID_PATTERN.exec(periodId);
  if (!match) return periodId;

  const prefixName = PERIOD_PREFIX_NAMES[match[1]];
  return prefixName ? `${prefixName} ${Number(match[2])}` : periodId;
}

function normalizePeriodId(id: unknown): string {
  return typeof id === 'string' ? id.trim().toLowerCase() : '';
}

function getPeriodIdPrefix(periodId: string): string | null {
  return PERIOD_ID_PATTERN.exec(periodId)?.[1] ?? null;
}

function comparePeriodIds(a: string, b: string): number {
  return a.localeCompare(b, undefined, { numeric: true });
}

function getItemPeriodIds(courseItems: ModuleItemType[]): string[] {
  const periodIds = courseItems.map((item) => normalizePeriodId(item.evaluation_period_id)).filter((periodId) => periodId !== '');
  return Array.from(new Set(periodIds));
}

function resolvePeriodLayout(evaluationPeriod: string | null | undefined, courseItems: ModuleItemType[]): PeriodLayout {
  const evaluationPeriodType = evaluationPeriod ?? EVALUATION_PERIODS.ANUAL;
  const periodType = findEvaluationPeriodType(evaluationPeriodType);
  const itemPeriodIds = getItemPeriodIds(courseItems);

  const configuredPeriods: PeriodDefinition[] = periodType?.periods ?? [];
  const configuredIds = new Set(configuredPeriods.map((period) => period.id));
  // Un id del mismo tipo que no está en la lista (q03, b05) tiene sección propia: si cayera en la primera,
  // el reorder le escribiría el id de esa sección (buildReorderPayload). Los de otro tipo ('annual' en un
  // curso trimestral) siguen cayendo en la primera a propósito, igual que antes de TAN-11212, y el anual
  // (su id no tiene prefijo) se queda con cualquier id.
  const typePrefix = getPeriodIdPrefix(configuredPeriods[0]?.id ?? '');
  const extraPeriodIds = periodType
    ? itemPeriodIds.filter((periodId) => typePrefix !== null && !configuredIds.has(periodId) && getPeriodIdPrefix(periodId) === typePrefix)
    : itemPeriodIds;
  extraPeriodIds.sort(comparePeriodIds);

  const definitions = [...configuredPeriods, ...extraPeriodIds.map((periodId) => ({ id: periodId, name: getPeriodSectionName(periodId) }))];

  return {
    normalizedType: normalizeEvaluationPeriod(evaluationPeriodType),
    isUnknownType: !periodType,
    extraPeriodIds,
    definitions: definitions.length > 0 ? definitions : [{ id: UNASSIGNED_PERIOD_ID, name: UNASSIGNED_PERIOD_NAME }],
  };
}

/** Ids de los items que `EVALUATION_PERIODS_TYPES` no cubre y han necesitado sección propia. */
export function getUncoveredPeriodDiagnostics(evaluationPeriod: string | null | undefined, courseItems: ModuleItemType[]): UncoveredPeriodDiagnostics {
  const { normalizedType, isUnknownType, extraPeriodIds } = resolvePeriodLayout(evaluationPeriod, courseItems);
  return { normalizedType, isUnknownType, extraPeriodIds };
}

/**
 * Agrupa los módulos en secciones de periodo, sin mutar la entrada. Nunca devuelve []: como mínimo, una
 * sección `UNASSIGNED_PERIOD_ID`; un item sin sección propia va a la primera.
 */
export function buildEvaluationPeriods(evaluationPeriod: string | null | undefined, courseItems: ModuleItemType[]): EvaluationPeriod[] {
  const { definitions } = resolvePeriodLayout(evaluationPeriod, courseItems);
  const sections: EvaluationPeriod[] = definitions.map(({ id, name }) => ({ id, name, units: [] }));

  courseItems.forEach((item) => {
    const itemPeriodId = normalizePeriodId(item.evaluation_period_id);
    const section = sections.find((period) => period.id === itemPeriodId) ?? sections[0];
    section.units.push({ ...item });
  });

  sections.forEach((section) => {
    section.units.sort((a, b) => a.order - b.order);
  });

  return sections;
}
